import React from "react";
import "./CollectionPostGridCard.css";
import { Badge, Grid } from "@shopify/polaris";
import {
  reelPostIcon,
  feedPostIcon,
  storyPostIcon,
  tiktokPostIcon,
  playIcon,
  analyticsGraphIcon,
} from "../../assets";
import ReactPlayer from "react-player";
import moment from "moment";
import CampSlider from "./widgets/CampSlider";
import { Box, ButtonBase, CircularProgress } from "@mui/material";
import UserProfile from "../../assets/user.png";
import DownloadIcon from "../../assets/downloadIconDark.svg";
import LinkIcon from "../../assets/linkIcon.svg";
import { formatCompact } from "./CollectionAnalytics";

// Same total the brand dashboard shows on its grid cards - keep the two
// formulas in sync so a shared collection reports identical numbers.
// Reach comes from impressions only; plays/views are reported
// inconsistently across platforms so they're ignored here.
const engagementTotal = (content) => {
  const stats = content.stats;
  if (stats == null) return 0;
  if (content.post_type === "STORY") return stats.impressions || 0;
  return (
    (stats.impressions || 0) +
    (stats.likes || 0) +
    (stats.comments || 0) +
    (stats.shares || 0)
  );
};

// Rows of the analytics sheet. `always` rows stay visible as "-" so every
// post reads identically; view/play counts only surface where the platform
// reports them.
const STAT_ROWS = [
  { key: "impressions", label: "Impressions", always: true },
  { key: "views", label: "Views" },
  { key: "likes", label: "Likes", always: true },
  { key: "comments", label: "Comments", always: true },
  { key: "shares", label: "Shares", always: true },
];

export default class CollectionPostGridCard extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      contentData: props.contentData,
      downloadMediaAPI: props.downloadMediaAPI,
      downloadingIds: [],
      statsOpenId: null,
    };
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleEscape);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleEscape);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.contentData !== this.props.contentData) {
      this.setState({ contentData: this.props.contentData });
    }
  }

  handleEscape = (event) => {
    if (event.key === "Escape" && this.state.statsOpenId != null) {
      this.closeStats();
    }
  };

  closeStats = () => this.setState({ statsOpenId: null });

  toggleStats = (post_id) => {
    this.setState((prev) => ({
      statsOpenId: prev.statsOpenId === post_id ? null : post_id,
    }));
  };

  renderStatsSheet = (content) => {
    const stats = content.stats || {};
    const updatedAt = stats.updated_at || content.stats_updated_at;

    return (
      <div className="stats_sheet_scrim__cpgc" onClick={this.closeStats}>
        <div
          className="stats_sheet__cpgc"
          role="dialog"
          aria-label="Post analytics"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="stats_sheet_head__cpgc">
            <ButtonBase
              className="stats_sheet_close__cpgc"
              aria-label="Close analytics"
              onClick={this.closeStats}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 14 14"
                aria-hidden="true"
              >
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ButtonBase>
            <span className="stats_sheet_title__cpgc">Analytics</span>
          </div>

          {updatedAt ? (
            <div className="stats_sheet_updated__cpgc">
              Last updated: {moment(updatedAt).format("MMM DD, YYYY")}
            </div>
          ) : null}

          <dl className="stats_sheet_rows__cpgc">
            {STAT_ROWS.filter((row) => row.always || stats[row.key] > 0).map(
              (row) => (
                <div className="stats_sheet_row__cpgc" key={row.key}>
                  <dt>{row.label}</dt>
                  <dd>
                    {stats[row.key] > 0 ? formatCompact(stats[row.key]) : "-"}
                  </dd>
                </div>
              ),
            )}
          </dl>
        </div>
      </div>
    );
  };

  handlePauseState = (_, post_id) => {
    const newPosts = this.state.contentData.map((post) =>
      post.id === post_id ? { ...post, is_video_playing: false } : { ...post },
    );

    this.setState({
      contentData: newPosts,
    });
  };

  handlePlayState = (_, post_id) => {
    const newPosts = this.state.contentData.map((post) =>
      post.id === post_id
        ? { ...post, is_video_playing: true }
        : { ...post, is_video_playing: false },
    );

    this.setState({
      contentData: newPosts,
    });
  };

  handleOnReadyState = (_, post_id) => {
    const newPosts = this.state.contentData.map((post) =>
      post.id === post_id
        ? { ...post, show_usage_rights: false, is_video_playing: true }
        : { ...post, is_video_playing: false },
    );

    this.setState({
      contentData: newPosts,
    });
  };

  // Replace image function
  replaceImage = (error) => {
    //replacement of broken Image
    error.target.src = UserProfile;
  };

  openOriginalPost = (content) => {
    if (content?.platform === "TIKTOK") {
      window.open(
        `https://www.tiktok.com/@${
          content?.creator?.username
        }/video/${content?.id.substring(3)}`,
      );
    } else {
      window.open(content?.media[0]?.permalink);
    }
  };

  handleDownload = (content) => {
    const { downloadMediaAPI, downloadingIds } = this.state;

    if (downloadingIds.includes(content.id)) return;

    this.setState({ downloadingIds: [...downloadingIds, content.id] });

    const stopLoading = () =>
      this.setState((prevState) => ({
        downloadingIds: prevState.downloadingIds.filter(
          (id) => id !== content.id,
        ),
      }));

    Promise.resolve(downloadMediaAPI(content.id, content))
      .catch(() => {})
      .finally(stopLoading);
  };

  renderPostCard = (content, index) => {
    const isDownloading = this.state.downloadingIds.includes(content.id);
    const engagement = engagementTotal(content);
    const statsOpen = this.state.statsOpenId === content.id;

    return (
      <div key={content.id || index} className="main-pc">
        <Box className="card_header_cpgc">
          {/* Post Card Header */}
          <div className="pc-header-inside container-set">
            <Box position="relative">
              <Box display="flex">
                <img
                  className="profile_image_cpgc"
                  src={content.creator.display}
                  alt={content.creator.username}
                  onError={(e) => this.replaceImage(e)}
                  width={34}
                  height={34}
                />
                <Box mt={0.4} ml={1}>
                  <Box className="profile_username__cpgc">
                    {content.creator.username}
                  </Box>
                  <Box className="profile_followers__cpgc">
                    {content.creator?.followers > 1000
                      ? Math.round((content.creator?.followers / 1000) * 10) /
                          10 +
                        "k followers"
                      : content.creator?.followers != null &&
                          content.creator?.followers != 0
                        ? `${content.creator?.followers} followers`
                        : ""}
                  </Box>
                </Box>
              </Box>
              <Box className="post_icon_on_profile__cpgc">
                <img
                  src={
                    content.platform === "TIKTOK"
                      ? tiktokPostIcon
                      : content.post_type === "REELS" ||
                          content.post_type === "IMAGE"
                        ? reelPostIcon
                        : content.post_type === "STORY"
                          ? storyPostIcon
                          : feedPostIcon
                  }
                  width={14}
                  height={14}
                  alt="post type"
                />
              </Box>
            </Box>
          </div>
        </Box>

        <Box className="content_actions_btn__cpgc">
          {content.post_type !== "STORY" && (
            <ButtonBase
              className="action_btn__cpgc"
              onClick={() => {
                this.openOriginalPost(content);
              }}
            >
              <img src={LinkIcon} width={15} height={15} alt="open post" />
            </ButtonBase>
          )}

          <ButtonBase
            className="action_btn__cpgc"
            disabled={isDownloading}
            onClick={() => {
              this.handleDownload(content);
            }}
          >
            {isDownloading ? (
              <CircularProgress size={15} sx={{ color: "#000000" }} />
            ) : (
              <img src={DownloadIcon} width={15} height={15} alt="download" />
            )}
          </ButtonBase>
        </Box>

        <Box className="rights_badges_box">
            {engagement > 0 ? (
            <ButtonBase
              className="post_stats__cpgc"
              aria-haspopup="dialog"
              aria-expanded={statsOpen}
              onClick={() => this.toggleStats(content.id)}
            >
              <img src={analyticsGraphIcon} width={16} height={16} alt="" />
              <span style={{color:"white"}}>{formatCompact(engagement)}</span>
            </ButtonBase>
          ) : null}
          {content.show_usage_rights ? (
            <Box className="usage_rights__pgc">
              {content.rights_status == "GRANTED" ? (
                <Badge size="small" tone="success">
                  Approved for Usage
                </Badge>
              ) : null}

              {content.rights_status == "DENIED" ? (
                <Badge size="small" tone="critical">
                  Usage rights declined
                </Badge>
              ) : null}
            </Box>
          ) : null}

        
        </Box>

        {statsOpen ? this.renderStatsSheet(content) : null}

        <Box className="post_time__cpgc">
          {moment(content.created_at).format("MMM DD")}
        </Box>

        <Box className="top_gradiant_overlay_cpgc">{/* Top Gradient */}</Box>

        {content.show_usage_rights != false ? (
          <Box className="bottom_gradiant_overlay_cpgc"></Box>
        ) : null}

        <div className="camp_cont__cpgc">
          {content.media_type == "VIDEO" ? (
            <ReactPlayer
              style={{ position: "absolute", top: 0, left: 0 }}
              className="react-player"
              controls={true}
              light={content?.media[0]?.thumbnail_url}
              playIcon={
                <img src={playIcon} height={50} width={50} alt="play" />
              }
              url={content?.media[0]?.media_url}
              width="100%"
              height="100%"
              playing={content?.is_video_playing}
              onReady={() => {
                this.handleOnReadyState(index, content?.id);
              }}
              onPause={() => {
                this.handlePauseState(index, content?.id);
              }}
              onPlay={() => {
                this.handlePlayState(index, content?.id);
              }}
              onClickPreview={() => {}}
              config={{
                file: {
                  attributes: {
                    style: {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    },
                  },
                },
              }}
            />
          ) : content.media_type == "IMAGE" ? (
            <img
              alt=""
              className="pc-body-image"
              height="100%"
              src={content?.media[0]?.media_url}
            />
          ) : (
            <CampSlider media={content.media} />
          )}
        </div>

        {/* Post Card Footer */}
        <div className="pc-footer-old container-set"></div>
      </div>
    );
  };

  render() {
    const { contentData } = this.state;

    return (
      <Box className="collection-grid-container">
        <Grid>
          {contentData.map((content, index) => (
            <Grid.Cell
              key={content.id || index}
              columnSpan={{
                xs: 6, // Mobile: 1 column (6/6 = full width)
                sm: 6, // Small: 1 column
                md: 5, // Medium: 2 columns (4/8)
                lg: 3, // Large: 3 columns (3/12)
                xl: 3, // Extra large: 4 columns (3/12)
              }}
            >
              {this.renderPostCard(content, index)}
            </Grid.Cell>
          ))}
        </Grid>
      </Box>
    );
  }
}
